"use client";

import { ArrowLeft, CirclePlay, Eye, Search, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useShell } from "@/components/shell/ShellProvider";
import { Avatar } from "@/components/ui/Avatar";
import type { Suggestion, SuggestionGroup, SuggestionType } from "@/lib/api/types";
import { cn } from "@/lib/cn";
import { compact } from "@/lib/format";
import { localePath } from "@/lib/i18n";
import { useLang, useUi } from "@/components/ui/use-ui";
import { fmt } from "@/lib/locales/fmt";

const TABS: { type: SuggestionType; key: "stars" | "productions" | "other" }[] = [
  { type: "people", key: "stars" },
  { type: "brands", key: "productions" },
  { type: "other", key: "other" },
];

export function SearchOverlay() {
  const { searchOpen, closeSearch } = useShell();
  if (!searchOpen) return null;
  return <SearchPanel onClose={closeSearch} />;
}

function SearchPanel({ onClose }: { onClose: () => void }) {
  const router = useRouter();
  const lang = useLang();
  const t = useUi();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [type, setType] = useState<SuggestionType | null>(null);
  const [items, setItems] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(-1);

  useEffect(() => {
    inputRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q: query.trim() });
        if (type) params.set("type", type);
        const res = await fetch(`/api/suggest?${params}`, { signal: ctrl.signal });
        const data: { items: Suggestion[] } = await res.json();
        setItems(data.items);
        setCursor(-1);
      } catch (err) {
        if ((err as Error).name !== "AbortError") setItems([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 140);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [query, type]);

  const groupLabel = (g: SuggestionGroup) =>
    ({ search: query.trim() ? t.search.searches : t.search.popular, category: t.search.categories, star: t.search.stars, production: t.search.productions, other: t.search.otherProfiles })[g];

  const go = (href: string) => {
    onClose();
    router.push(localePath(lang, href));
  };

  const submit = () => {
    if (cursor >= 0 && items[cursor]) return go(items[cursor].href);
    const q = query.trim();
    if (q) go(`/search?q=${encodeURIComponent(q)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(items.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(-1, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={t.nav.search} onKeyDown={onKeyDown}>
      <div className="absolute inset-0 bg-black/35" onClick={onClose} aria-hidden />
      <div className="glass-strong absolute left-1/2 top-0 flex max-h-dvh w-full -translate-x-1/2 flex-col overflow-hidden shadow-[0_30px_90px_-20px_rgb(0_0_0/0.9)] sm:top-3 sm:max-h-[calc(100dvh-24px)] sm:w-[min(560px,calc(100%-24px))] sm:rounded-xl">
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-line px-3">
          <button type="button" aria-label={t.nav.closeSearch} onClick={onClose} className="grid size-9 place-items-center rounded-full text-soft hover:bg-white/10 hover:text-fg">
            <ArrowLeft size={20} strokeWidth={1.8} />
          </button>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`${t.nav.search} ...`}
            aria-label={t.search.placeholder}
            enterKeyHint="search"
            className="h-full min-w-0 flex-1 bg-transparent text-[16px] text-fg outline-none placeholder:text-muted"
          />
          {query && (
            <button type="button" aria-label={t.cams.clear} onClick={() => setQuery("")} className="grid size-8 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-fg">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-1 px-3 pb-1 pt-3">
          {TABS.map((tab) => (
            <button
              key={tab.type}
              type="button"
              aria-pressed={type === tab.type}
              onClick={() => setType((t) => (t === tab.type ? null : tab.type))}
              className={cn(
                "rounded-full px-3 py-1.5 text-[14px] transition-colors",
                type === tab.type ? "bg-white text-black" : "text-fg hover:bg-white/10",
              )}
            >
              {t.search[tab.key]}
            </button>
          ))}
        </div>

        <ul className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-2 pb-2" role="listbox">
          {items.map((s, i) => (
            <li key={`${s.group}-${s.slug}`} role="option" aria-selected={cursor === i}>
              {/* Each group under its heading: searches, categories, stars, productions (not within a tab). */}
              {!type && s.group !== items[i - 1]?.group && (
                <p className={cn("px-2 pb-1 text-[12px] uppercase tracking-[0.08em] text-faint", i > 0 ? "pt-4" : "pt-2")}>{groupLabel(s.group)}</p>
              )}
              {s.group === "search" ? (
                <button
                  type="button"
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => go(s.href)}
                  className={cn("flex w-full items-center gap-3.5 rounded-lg px-3 py-2 text-left transition-colors", cursor === i && "bg-white/[0.07]")}
                >
                  <Search size={17} className="shrink-0 text-muted" />
                  <span className="min-w-0 flex-1 truncate text-[16px] text-fg">{s.name}</span>
                  {s.videoCount > 0 && (
                    <span className="flex shrink-0 items-center gap-1 text-[14px] text-muted">
                      {compact(s.videoCount)}
                      <CirclePlay size={13} strokeWidth={1.8} />
                    </span>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => go(s.href)}
                  className={cn("flex w-full items-center gap-3.5 rounded-lg px-2 py-2.5 text-left transition-colors", cursor === i && "bg-white/[0.07]")}
                >
                  <Avatar name={s.name} src={s.avatar} size={52} variant={s.type === "people" ? "person" : s.type === "brands" ? "brand" : "letter"} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[16px] text-fg">{s.name}</span>
                    {s.description && <span className="mt-0.5 block truncate text-[14px] text-muted">{s.description}</span>}
                  </span>
                  <span className="shrink-0 text-right">
                    {/* Followers once there are any, else the views of their videos. */}
                    {s.followers ? (
                      <span className="block text-[16px] text-fg">{compact(s.followers)}</span>
                    ) : s.views ? (
                      <span className="flex items-center justify-end gap-1 text-[16px] text-fg">
                        {compact(s.views)}
                        <Eye size={14} strokeWidth={1.8} className="text-muted" />
                      </span>
                    ) : null}
                    <span className="mt-0.5 flex items-center justify-end gap-1 text-[14px] text-muted">
                      {compact(s.videoCount)}
                      <CirclePlay size={13} strokeWidth={1.8} />
                    </span>
                  </span>
                </button>
              )}
            </li>
          ))}
          {!loading && items.length === 0 && <li className="px-3 py-10 text-center text-[14px] text-muted">{fmt(t.search.nothingFor, { q: query.trim() })}</li>}
        </ul>

        {query.trim() && (
          <button
            type="button"
            onClick={() => go(`/search?q=${encodeURIComponent(query.trim())}`)}
            className="flex h-12 shrink-0 items-center gap-3 border-t border-line px-5 text-[14px] text-soft hover:bg-white/[0.05] hover:text-fg"
          >
            <Search size={16} />
            {fmt(t.search.searchFor, { q: query.trim() })}
          </button>
        )}
      </div>
    </div>
  );
}
